import { cn } from "lib/utils";
import React, { ReactNode } from "react";

interface BadgeProps {
  label: string;
  variant?:
    | "primary"
    | "payer"
    | "insured"
    | "isPrimary"
    | "isPayer"
    | "isInsured"
    | "blueChip"
    | "lightBlue"
    | "redChip"
    | "elite"
    | "premium"
    | "starter"
    | "subscriptionDefault"
    | "active"
    | "inactive"
    | "fulfilling"
    | "declined"
    | "client"
    | "lead"
    | "confirmed"
    | "completed"
    | "approved"
    | "pending"
    | "deleted"
    | "skipped"
    | "viewed";
  className?: string;
  style?: React.CSSProperties;
  /** Shown before the label, e.g. a check icon on "Included in Plan". */
  icon?: ReactNode;
}

const blueChipStyles = "!border-[#1890ff] text-[#1890ff] bg-[#e6f7ff]";
const redChipStyles = "!border-[#f5222d] text-[#f5222d] bg-[#fff1f0]";
const primaryBadgeStyles = "!border-[#004781] text-[#004781]";
const payerBadgeStyles = "!border-[#1EA97C] text-[#1EA97C]";
const insuredBadgeStyles = "!border-[#1581ED] text-[#1581ED]";

/** Default: uppercase. Override with className e.g. "lowercase" or "normal-case". */
export function CustomBadge({ label, variant = "isPrimary", className, style, icon }: BadgeProps) {
  const variantStyles: Record<NonNullable<BadgeProps["variant"]>, string> = {
    primary: primaryBadgeStyles,
    payer: payerBadgeStyles,
    insured: insuredBadgeStyles,
    isPrimary: primaryBadgeStyles,
    isPayer: payerBadgeStyles,
    isInsured: insuredBadgeStyles,
    blueChip: blueChipStyles,
    // Light-blue fill and border, brand-blue text (Plan Design's tags).
    lightBlue: "!border-switch-button-bg !text-brand-primary-blue bg-backGround",
    redChip: redChipStyles,
    viewed: "!border-[#004781] !text-[#004781] !bg-[#BDCFDE]",
    elite: "!border-[#004781] !text-[#004781] bg-[#E5EDF2]",
    premium: "!border-[#1581ED] !text-[#1581ED] bg-[#F5F9FF]",
    active: "!border-[#1581ED] !text-[#1581ED] bg-[#F5F9FF]",
    starter: "!border-[#616161] !text-[#616161] bg-[#F5F5F5]",
    inactive: "!border-[#616161] !text-[#616161] bg-[#F5F5F5]",
    lead: "!border-[#616161] !text-[#616161] bg-[#F5F5F5]",
    fulfilling: "!border-[#1581ED] !text-[#1581ED] bg-[#F5F9FF]",
    subscriptionDefault: "!border-[#8C8C8C] !text-[#6B6B6B] bg-[#F7F7F7]",
    declined: redChipStyles,
    client: primaryBadgeStyles,
    confirmed: "!border-[#1EA97C] !text-[#1EA97C] !bg-[#E4F8F0]",
    completed: "!border-[#1EA97C] !text-[#1EA97C] !bg-[#E4F8F0]",
    deleted: "!border-[#1EA97C] !text-[#1EA97C] !bg-[#E4F8F0]",
    approved: "!border-[#1EA97C] !text-[#1EA97C] !bg-[#E4F8F0]",
    skipped: "!border-[#757575] !text-[#757575] !bg-[#F5F5F5]",
    pending: "!border-[#C79807] !text-[#C79807] !bg-[#FEFBF3]",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center !px-2 !py-[0.8px] rounded-full text-xs font-semibold border-1 !border-solid max-w-full truncate sm:whitespace-normal sm:truncate-0 break-words uppercase",
        variantStyles[variant],
        className,
      )}
      style={style}
      key={React?.useId()}
    >
      {icon}
      {label}
    </span>
  );
}
